/**
 * PairMachinePage — add a machine to the environments list from the browser
 * (forge#3520).
 *
 * Mints a one-time pairing credential, renders it as a QR code plus a copyable
 * `/login#token=…` URL, and watches for the moment the machine signs in. The
 * credential never leaves the hash fragment in transit, so the server log and
 * the network tab never see it. Revoking the link kills the credential
 * everywhere.
 *
 * The linked signal is attribution-guarded on two axes. The page reads the
 * client-session list BEFORE the credential exists (the baseline session
 * ids), and every mint carries a unique label that the server copies onto the
 * client session created when that link is redeemed. "Linked" therefore means
 * "a session id outside the baseline, wearing this link's label", so an
 * unrelated login matches neither half and can never claim the link. A failed
 * baseline read never defaults to zero; the page stays in an explicit
 * checking state and retries with backoff. A return trip to this page within
 * the link's lifetime restores the stored pending link instead of minting a
 * second credential.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { useNavigate } from "@tanstack/react-router";

import {
  createServerPairingCredential,
  listServerClientSessions,
  revokeServerPairingLink,
} from "~/environments/primary";
import { normalizeExpiresAt, resolvePairingUrl } from "~/lib/pairingPresentation";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import {
  clearPendingPairingLink,
  isPendingPairingLinkExpired,
  loadPendingPairingLink,
  savePendingPairingLink,
} from "./pairMachinePendingLink";
import {
  PairMachineCheckingCard,
  PairMachineErrorCard,
  PairMachineLinkedCard,
  PairMachineLoadingCard,
  PairMachineReadyActions,
  PairMachineReadyCard,
  PairMachineRevokedCard,
} from "./pairMachineCards";

const LINKED_POLL_INTERVAL_MS = 3_000;
const BASELINE_RETRY_BASE_MS = 1_000;
const BASELINE_RETRY_CAP_MS = 8_000;
const BASELINE_MAX_ATTEMPTS = 5;

/** The unique label each mint carries. The server stamps it on the session
 * the link creates, which is what makes the later poll attributable: a new
 * session id alone would also match any unrelated login. */
function createPairingLabel(): string {
  const randomId = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  return `Desktop machine ${randomId.slice(0, 8)}`;
}

/**
 * State machine for one visit. `checking-baseline` is the honest answer to
 * "is the server reachable enough to mint an attributable link": nothing is
 * minted, and linked is never evaluated, until the baseline lands.
 */
type PairMachineState =
  | { readonly status: "checking-baseline"; readonly attempts: number }
  | { readonly status: "loading" }
  | {
      readonly status: "ready";
      readonly linkId: string;
      readonly pairingUrl: string;
      readonly expiresAt: string;
      /** The unique label this link was minted with; the poll matches it. */
      readonly label: string;
      /** Session ids observed before the credential existed. */
      readonly baselineSessionIds: readonly string[];
      /** True when this pending link came back from a previous visit. */
      readonly restored: boolean;
    }
  | { readonly status: "linked" }
  | { readonly status: "revoked" }
  | { readonly status: "error"; readonly message: string };

export interface ClientSessionRecord {
  readonly sessionId: string;
  /** The part of the session's client metadata the linked signal reads. */
  readonly client: { readonly label?: string };
}

/**
 * The linked signal, in server-side identity terms. The server copies the
 * pairing link's label onto the client session created by redeeming it, so
 * the machine's sign-in is a session whose id cannot have been in the
 * pre-mint baseline AND whose client label is this link's label. An unrelated
 * login (another device, a second pairing link) fails at least one half and
 * can never claim the link. Comparing session IDS (server-issued) — never a
 * browser clock against server timestamps, which are different clock domains
 * and cannot be ordered.
 */
export function hasLinkedSession(
  sessions: readonly ClientSessionRecord[],
  baselineSessionIds: readonly string[],
  linkLabel: string,
): boolean {
  const known = new Set(baselineSessionIds);
  return sessions.some(
    (session) => !known.has(session.sessionId) && session.client.label === linkLabel,
  );
}

export function PairMachinePage() {
  const navigate = useNavigate();
  const [state, setState] = useState<PairMachineState>({
    status: "checking-baseline",
    attempts: 0,
  });
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  /** Set when this visit resumed a stored link; the baseline loop must not
   * mint (or re-restore) over it. */
  const restoredRef = useRef(false);

  useEffect(() => {
    // A stored pending link from this tab resumes as the pending banner, with
    // its Revoke button, until it links or expires. Anything expired or
    // unreadable is dropped so the visit mints fresh.
    const pending = loadPendingPairingLink(window.sessionStorage);
    if (pending !== null && !isPendingPairingLinkExpired(pending, Date.now())) {
      restoredRef.current = true;
      setState({
        status: "ready",
        linkId: pending.linkId,
        pairingUrl: pending.pairingUrl,
        expiresAt: pending.expiresAt,
        label: pending.label,
        baselineSessionIds: pending.baselineSessionIds,
        restored: true,
      });
      return;
    }
    if (pending !== null) {
      clearPendingPairingLink(window.sessionStorage);
    }
  }, []);

  useEffect(() => {
    if (state.status !== "checking-baseline" || restoredRef.current) return;
    let cancelled = false;
    const attempt = state.attempts;
    const read = async () => {
      try {
        // The baseline MUST land before minting: it is what makes "linked"
        // attributable to this credential instead of to any concurrent login.
        // The baseline is the SERVER-ISSUED session ids, so the later
        // comparison never crosses a clock domain.
        const baselineSessionIds = await listServerClientSessions().then((sessions) =>
          sessions.map((session) => session.sessionId),
        );
        if (cancelled) return;
        // The unique label is the attribution hook: the server stamps it on
        // the session this credential's redemption creates, so the poll can
        // tell this link's machine from any concurrent login.
        const label = createPairingLabel();
        const credential = await createServerPairingCredential(label, "owner");
        if (cancelled) return;
        const pairingUrl = await resolvePairingUrl(credential.credential);
        savePendingPairingLink(window.sessionStorage, {
          linkId: credential.id,
          pairingUrl,
          expiresAt: normalizeExpiresAt(credential.expiresAt),
          label,
          baselineSessionIds,
        });
        setState({
          status: "ready",
          linkId: credential.id,
          pairingUrl,
          expiresAt: normalizeExpiresAt(credential.expiresAt),
          label,
          baselineSessionIds,
          restored: false,
        });
      } catch (error) {
        if (cancelled) return;
        if (attempt + 1 >= BASELINE_MAX_ATTEMPTS) {
          setState({
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Could not reach the server to start a pairing link.",
          });
          return;
        }
        // A failed baseline is never a zero. Re-read with backoff and stay in
        // the explicit checking state; minting waits for an attributable base.
        const delay = Math.min(BASELINE_RETRY_BASE_MS * 2 ** attempt, BASELINE_RETRY_CAP_MS);
        setTimeout(() => {
          if (!cancelled) setState({ status: "checking-baseline", attempts: attempt + 1 });
        }, delay);
      }
    };
    void read();
    return () => {
      cancelled = true;
    };
  }, [state]);

  useEffect(() => {
    if (state.status !== "ready") return;
    // Late resolutions after revoke (or unmount) must not flip the page back
    // to linked or resurrect the cleared pending record.
    let cancelled = false;
    const poll = setInterval(() => {
      if (
        isPendingPairingLinkExpired(
          {
            linkId: state.linkId,
            pairingUrl: state.pairingUrl,
            expiresAt: state.expiresAt,
            label: state.label,
            baselineSessionIds: state.baselineSessionIds,
          },
          Date.now(),
        )
      ) {
        clearPendingPairingLink(window.sessionStorage);
        setState({ status: "error", message: "The pairing link expired. Create a new one." });
        return;
      }
      void listServerClientSessions()
        .then((sessions) => {
          if (cancelled) return;
          if (hasLinkedSession(sessions, state.baselineSessionIds, state.label)) {
            clearPendingPairingLink(window.sessionStorage);
            setState({ status: "linked" });
          }
        })
        .catch(() => {
          // Transient poll failures keep the link waiting; the expiry and the
          // revoke button remain the honest exits.
        });
    }, LINKED_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [state]);

  async function handleRevoke() {
    if (state.status !== "ready") return;
    try {
      const revoked = await revokeServerPairingLink(state.linkId);
      clearPendingPairingLink(window.sessionStorage);
      setState(
        revoked
          ? { status: "revoked" }
          : { status: "error", message: "The pairing link is no longer available." },
      );
    } catch (error) {
      setState({
        status: "error",
        message: error instanceof Error ? error.message : "Could not revoke the pairing link.",
      });
    }
  }

  return (
    <PairMachineView
      state={state}
      isCopied={isCopied}
      copyToClipboard={(value) => copyToClipboard(value, undefined)}
      onRevoke={() => void handleRevoke()}
      onViewEnvironments={() => void navigate({ to: "/settings/environments" })}
    />
  );
}

/** The rendered page for a given pairing state. The orchestrator above owns
 * only the mint, the poll, and the revoke; everything drawn is decided here. */
function PairMachineView({
  state,
  isCopied,
  copyToClipboard,
  onRevoke,
  onViewEnvironments,
}: {
  readonly state: PairMachineState;
  readonly isCopied: boolean;
  readonly copyToClipboard: (value: string) => void;
  readonly onRevoke: () => void;
  readonly onViewEnvironments: () => void;
}) {
  return (
    <main className="flex min-h-full flex-1 items-center justify-center bg-background px-6 py-12 text-foreground">
      <section className="flex w-full max-w-[360px] flex-col items-center text-center">
        <h1 className="text-[22px] font-semibold leading-7 tracking-tight">Add a machine</h1>
        <div className="mt-6 flex w-full justify-center">
          {pairMachineCard(state, onViewEnvironments)}
        </div>
        {state.status === "ready" ? (
          <PairMachineReadyActions
            pairingUrl={state.pairingUrl}
            copyToClipboard={copyToClipboard}
            isCopied={isCopied}
            onRevoke={onRevoke}
          />
        ) : null}
      </section>
    </main>
  );
}

/** The card for the current pairing state, or null while the mint is in
 * flight (the loading card covers that). */
function pairMachineCard(state: PairMachineState, onViewEnvironments: () => void): ReactElement {
  switch (true) {
    case state.status === "checking-baseline":
      return <PairMachineCheckingCard />;
    case state.status === "error":
      return <PairMachineErrorCard message={state.message} />;
    case state.status === "linked":
      return <PairMachineLinkedCard onViewEnvironments={onViewEnvironments} />;
    case state.status === "revoked":
      return <PairMachineRevokedCard />;
    case state.status === "ready":
      return (
        <PairMachineReadyCard
          pairingUrl={state.pairingUrl}
          expiresLabel={new Intl.DateTimeFormat(undefined, {
            hour: "numeric",
            minute: "2-digit",
          }).format(new Date(state.expiresAt))}
          restored={state.restored}
        />
      );
    default:
      return <PairMachineLoadingCard />;
  }
}
